import { createFileRoute, useNavigate, useParams } from "@tanstack/react-router";
import { CountryCode, getCountries, isValidPhoneNumber } from "libphonenumber-js";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import Button from "@/components/core/Buttons/Button";
import InputField from "@/components/core/FormControls/InputField";
import PhoneInput from "@/components/core/PhoneInput/PhoneInput";
import { useTagManager } from "@/hooks/useTagManager";
import { requireAuthWithDevice } from "@/lib/guards";
import { platformApi } from "@/services";
import { useStore } from "@/store";
import { logError } from "@/utils/logging/error";

export const Route = createFileRoute("/$locale/$partner/form")({
	beforeLoad: requireAuthWithDevice,
	component: Form,
});

const countryCodes: readonly string[] = getCountries();
function isCountryCode(value: string | undefined | null): value is CountryCode {
	if (!value) return false;
	return countryCodes.includes(value);
}

type UserInput = {
	fullName?: string;
	email?: string;
	phoneNumber?: string;
	countryCode?: string;
	iban?: string;
};

function Form() {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { locale: routeLocale, partner: routePartner } = useParams({ from: "/$locale/$partner" });
	const setCustomerInfo = useStore((state) => state.setCustomerInfo);
	const fullName = useStore((state) => state.fullName);
	const email = useStore((state) => state.email);
	const phoneNumber = useStore((state) => state.phoneNumber) ?? "";
	const sessionId = useStore((state) => state.sessionId) ?? "";
	const countryCode = useStore((state) => state.countryCode);
	const hasUserConsent = useStore((state) => state.consent);
	const locale = useStore((state) => state.location?.locale);
	const defaultCountryCode = locale?.split("-")[1]?.toUpperCase();

	const effectiveCountryCode = countryCode ?? defaultCountryCode;
	const validatedCountryCode = isCountryCode(effectiveCountryCode) ? effectiveCountryCode : undefined;
	const isPhoneValid = validatedCountryCode ? isValidPhoneNumber(phoneNumber, validatedCountryCode) : false;
	useTagManager("Step Form");

	useEffect(() => {
		if (sessionId) {
			const fetchUserInfo = async () => {
				try {
					const userData = await platformApi.getTransient<UserInput>(sessionId, "userInput");
					if (userData) {
						setCustomerInfo({
							fullName: userData.fullName,
							email: userData.email,
							phoneNumber: userData.phoneNumber,
							countryCode: isCountryCode(userData.countryCode) ? userData.countryCode : undefined,
							iban: userData.iban,
						});
					}
				} catch (error) {
					logError(error, "Could not fetch user info");
				}
			};
			fetchUserInfo();
		}
	}, [sessionId, setCustomerInfo]);

	const isValidEmail = (email: string | null) => {
		return z.string().email().safeParse(email).success;
	};

	const isValidFullname = (fullName: string | null) => {
		const fullNameSchema = z
			.string()
			.min(3)
			.refine((name) => name.trim().split(" ").filter(Boolean).length >= 2);

		return fullNameSchema.safeParse(fullName).success;
	};

	const invalidEmailError =
		isValidEmail(email) || email === null || email === "" ? null : t("InputField.Invalid Email");

	const invalidFullNameError =
		isValidFullname(fullName) || fullName === null || fullName === "" ? null : t("InputField.Invalid FullName");

	const isButtonEnabled = phoneNumber !== "" && isPhoneValid && isValidEmail(email) && isValidFullname(fullName);

	return (
		<div className="mx-auto max-w-[81rem]">
			<div className="max-w-[35rem] m-auto">
				<h1 className="text-center text-3xl font-semibold mt-16 mb-9">{t("CustomerFormPage.Customer Info")}</h1>
				<div className="mb-5">
					<InputField
						placeholder={t("InputField.FullName")}
						label={t("InputField.FullName")}
						type="text"
						align="left"
						error={invalidFullNameError}
						value={fullName ?? ""}
						onChange={(e) => {
							setCustomerInfo({ fullName: e.target.value });
						}}
					/>
				</div>
				<div className="mb-4">
					<InputField
						placeholder={t("InputField.Email")}
						type="email"
						value={email ?? ""}
						error={invalidEmailError}
						align="left"
						label={t("InputField.Email")}
						onChange={(e) => {
							setCustomerInfo({ email: e.target.value });
						}}
					/>
				</div>
				<div className="mb-5">
					<PhoneInput
						placeholder={t("InputField.Phone Number") ?? ""}
						align="left"
						label={t("InputField.Phone Number") ?? ""}
						onDone={(value) => {
							setCustomerInfo({ phoneNumber: value });
						}}
						onCountryCodeChange={(code: string) => {
							if (isCountryCode(code)) {
								setCustomerInfo({ countryCode: code });
							}
						}}
						hasError={Boolean(!isPhoneValid && phoneNumber && phoneNumber !== "" && countryCode !== undefined)}
					/>
				</div>
				<div className="mx-auto w-fit mt-12">
					<Button
						label={hasUserConsent ? t("Buttons.Finalise Transaction") : t("Buttons.Next")}
						size="large"
						disabled={!isButtonEnabled}
						onClick={() => {
							navigate({ to: "/$locale/$partner/signature", params: { locale: routeLocale, partner: routePartner } });
						}}
					/>
				</div>
			</div>
		</div>
	);
}
